import { useCallback, useEffect, useRef } from 'react';

export interface IUseDebounceCallback {
  debounced: (...args: unknown[]) => void;
  cancel: VoidFunction;
}

export const useDebounceCallback = (
  callback: (...args: unknown[]) => void,
  timeToWait?: number,
): IUseDebounceCallback => {
  const callbackArgsRef = useRef<unknown[]>();
  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const callbackRef = useRef(callback);

  const cleanup = useCallback(() => {
    if (timeout.current) clearTimeout(timeout.current);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const debounced = useCallback(
    (...args: unknown[]): void => {
      cleanup();

      callbackArgsRef.current = args;
      timeout.current = setTimeout(() => {
        if (callbackArgsRef.current) callbackRef.current(...callbackArgsRef.current);
      }, timeToWait);
    },
    [cleanup, timeToWait],
  );

  return { debounced, cancel: cleanup };
};
